import soccer from "@/assets/athlete-soccer.jpg";
import tennis from "@/assets/athlete-tennis.jpg";
import basketball from "@/assets/athlete-basketball.jpg";
import surf from "@/assets/athlete-surf.jpg";
import boxing from "@/assets/athlete-boxing.jpg";
import gymnast from "@/assets/athlete-gymnast.jpg";

export type Cause = {
  id?: string;
  name: string;
  blurb: string;
  raised: string;
  goal: string;
  donateUrl?: string | null;
  logoUrl?: string | null;
  galleryUrls?: string[];
  athletes?: { id: string; name: string }[];
};

export type Brand = {
  id?: string;
  name: string;
  category: string;
  product: string;
  price: string;
  productUrl?: string | null;
  quote?: string | null;
  imageUrl?: string | null;
  galleryUrls?: string[];
  isHouseBrand?: boolean;
  athletes?: { id: string; name: string }[];
};

export type Athlete = {
  id: string;
  name: string;
  sport: string;
  country: string;
  image: string;
  tagline: string;
  bio: string;
  stats: { label: string; value: string }[];
  causes: Cause[];
  brands: Brand[];
};

export const athletes: Athlete[] = [
  {
    id: "maya-reyes",
    name: "Maya Reyes",
    sport: "Soccer",
    country: "USA",
    image: soccer,
    tagline: "Midfielder. Coach. Mother of two.",
    bio: "World Cup veteran turned youth-league founder. Maya is rewriting what longevity in sport looks like for women.",
    stats: [
      { label: "Caps", value: "142" },
      { label: "Goals", value: "37" },
      { label: "Trophies", value: "9" },
    ],
    causes: [
      { name: "Girls Play Free", blurb: "Free youth soccer programs in underserved cities.", raised: "$182k", goal: "$250k" },
      { name: "Maternal Athlete Fund", blurb: "Supporting moms returning to elite sport.", raised: "$54k", goal: "$100k" },
    ],
    brands: [
      { name: "Nike", category: "Apparel", product: "Phantom GX Boots", price: "$249" },
      { name: "Goodr", category: "Eyewear", product: "Field Visor", price: "$45" },
    ],
  },
  {
    id: "ines-laurent",
    name: "Inès Laurent",
    sport: "Tennis",
    country: "France",
    image: tennis,
    tagline: "Top 10. Left-handed. Unbothered.",
    bio: "Two-time Slam finalist with a backhand the tour can't read. Off court, she funds equal-prize-money campaigns.",
    stats: [
      { label: "Titles", value: "11" },
      { label: "Aces '24", value: "412" },
      { label: "Ranking", value: "#7" },
    ],
    causes: [
      { name: "Equal Purse Project", blurb: "Closing prize-money gaps at WTA 500 events.", raised: "$96k", goal: "$200k" },
    ],
    brands: [
      { name: "Wilson", category: "Gear", product: "Blade 98 Racket", price: "$269" },
      { name: "Lacoste", category: "Apparel", product: "Court Polo", price: "$120" },
    ],
  },
  {
    id: "amara-okonkwo",
    name: "Amara Okonkwo",
    sport: "Basketball",
    country: "Nigeria",
    image: basketball,
    tagline: "Forward. Founder. Force.",
    bio: "WNBA All-Star and founder of Hoops Lagos — a court-building nonprofit reshaping access across West Africa.",
    stats: [
      { label: "PPG", value: "21.4" },
      { label: "RPG", value: "9.1" },
      { label: "All-Star", value: "4x" },
    ],
    causes: [
      { name: "Hoops Lagos", blurb: "Building outdoor courts across Nigerian cities.", raised: "$310k", goal: "$500k" },
      { name: "Period Equity", blurb: "Free menstrual products for student athletes.", raised: "$68k", goal: "$120k" },
    ],
    brands: [
      { name: "Adidas", category: "Footwear", product: "Harden Vol. 8", price: "$160" },
      { name: "Oakley", category: "Eyewear", product: "Sutro Lite", price: "$176" },
    ],
  },
  {
    id: "kai-nakamura",
    name: "Kai Nakamura",
    sport: "Surf",
    country: "Japan",
    image: surf,
    tagline: "Big-wave chaser. Ocean defender.",
    bio: "Pipeline finalist and marine-conservation activist. Kai surfs the swells most won't paddle into.",
    stats: [
      { label: "Biggest", value: "42ft" },
      { label: "Tour wins", value: "6" },
      { label: "Reefs saved", value: "12" },
    ],
    causes: [
      { name: "Save Our Reefs", blurb: "Restoring coral systems in the Pacific.", raised: "$145k", goal: "$300k" },
    ],
    brands: [
      { name: "Patagonia", category: "Wetsuits", product: "R3 Yulex Suit", price: "$549" },
      { name: "Sun Bum", category: "Sunscreen", product: "Reef-Safe SPF 50", price: "$16" },
    ],
  },
  {
    id: "sofia-marquez",
    name: "Sofía Márquez",
    sport: "Boxing",
    country: "Mexico",
    image: boxing,
    tagline: "Featherweight champ. Loud and proud.",
    bio: "Undefeated featherweight world champion who turned her hometown gym into a sanctuary for young women.",
    stats: [
      { label: "Record", value: "24-0" },
      { label: "KOs", value: "18" },
      { label: "Belts", value: "3" },
    ],
    causes: [
      { name: "Punch Back", blurb: "Self-defense classes for women in Mexico City.", raised: "$72k", goal: "$150k" },
    ],
    brands: [
      { name: "Everlast", category: "Gear", product: "Pro Style Gloves", price: "$89" },
      { name: "Reebok", category: "Footwear", product: "Boxing Boot II", price: "$130" },
    ],
  },
  {
    id: "lin-chen",
    name: "Lin Chen",
    sport: "Gymnastics",
    country: "Taiwan",
    image: gymnast,
    tagline: "Beam queen. Mental-health advocate.",
    bio: "Olympic medalist redefining gymnastics culture by championing rest, therapy, and athlete autonomy.",
    stats: [
      { label: "Olympics", value: "2x" },
      { label: "Medals", value: "5" },
      { label: "PB Beam", value: "15.3" },
    ],
    causes: [
      { name: "Mind Over Medal", blurb: "Free therapy access for elite young athletes.", raised: "$210k", goal: "$400k" },
    ],
    brands: [
      { name: "GK Elite", category: "Apparel", product: "Signature Leotard", price: "$98" },
      { name: "Calm", category: "Wellness", product: "Annual Subscription", price: "$70" },
    ],
  },
];

export const getAthlete = (id: string) => athletes.find((a) => a.id === id);
